import type { Tile2048 } from '@/types/games';

export const TILES_2048: Record<number, Tile2048> = {
  2: { value: 2, label: 'Cellule', color: 'bg-amber-100', textColor: 'text-foreground-950' },
  4: { value: 4, label: 'Tissu', color: 'bg-amber-200', textColor: 'text-foreground-950' },
  8: { value: 8, label: 'Organe', color: 'bg-orange-200', textColor: 'text-foreground-950' },
  16: { value: 16, label: 'Système', color: 'bg-orange-300', textColor: 'text-foreground-950' },
  32: { value: 32, label: 'Corps', color: 'bg-red-300', textColor: 'text-foreground-950' },
  64: { value: 64, label: 'Santé', color: 'bg-red-400', textColor: 'text-background-50' },
  128: { value: 128, label: 'Vitalité', color: 'bg-green-300', textColor: 'text-foreground-950' },
  256: { value: 256, label: 'Énergie', color: 'bg-green-400', textColor: 'text-background-50' },
  512: { value: 512, label: 'Force', color: 'bg-emerald-400', textColor: 'text-background-50' },
  1024: { value: 1024, label: 'Résistance', color: 'bg-emerald-500', textColor: 'text-background-50' },
  2048: { value: 2048, label: 'Immunité', color: 'bg-emerald-600', textColor: 'text-background-50' },
};

export const INIT_2048_BOARD = (): (number | null)[][] => {
  const board: (number | null)[][] = Array(4).fill(null).map(() => Array(4).fill(null));
  addRandomTile(board);
  addRandomTile(board);
  return board;
};

function addRandomTile(board: (number | null)[][]) {
  const empty: [number, number][] = [];
  for (let r = 0; r < 4; r++) {
    for (let c = 0; c < 4; c++) {
      if (board[r][c] === null) empty.push([r, c]);
    }
  }
  if (empty.length > 0) {
    const [r, c] = empty[Math.floor(Math.random() * empty.length)];
    board[r][c] = Math.random() < 0.9 ? 2 : 4;
  }
}

export function moveBoard(board: (number | null)[][], direction: 'up' | 'down' | 'left' | 'right'): { board: (number | null)[][]; score: number; moved: boolean } {
  const size = 4;
  let score = 0;
  let moved = false;

  const rotated = rotateForDirection(board, direction);

  const newBoard: (number | null)[][] = [];
  for (let r = 0; r < size; r++) {
    const row = rotated[r].filter((v): v is number => v !== null);
    const merged: (number | null)[] = [];
    let i = 0;
    while (i < row.length) {
      if (i + 1 < row.length && row[i] === row[i + 1]) {
        merged.push(row[i] * 2);
        score += row[i] * 2;
        i += 2;
      } else {
        merged.push(row[i]);
        i++;
      }
    }
    while (merged.length < size) merged.push(null);
    newBoard.push(merged);
  }

  if (JSON.stringify(rotated) !== JSON.stringify(newBoard)) moved = true;

  const result = rotateBack(newBoard, direction);
  if (moved) addRandomTile(result);
  return { board: result, score, moved };
}

function rotateForDirection(board: (number | null)[][], dir: string): (number | null)[][] {
  const size = 4;
  const rotated: (number | null)[][] = Array(size).fill(null).map(() => Array(size).fill(null));
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      if (dir === 'left') rotated[r][c] = board[r][c];
      else if (dir === 'right') rotated[r][c] = board[r][size - 1 - c];
      else if (dir === 'up') rotated[r][c] = board[c][r];
      else if (dir === 'down') rotated[r][c] = board[size - 1 - c][r];
    }
  }
  return rotated;
}

function rotateBack(board: (number | null)[][], dir: string): (number | null)[][] {
  if (dir === 'left') return board;
  if (dir === 'right') return board.map(row => row.slice().reverse());
  const size = 4;
  const result: (number | null)[][] = Array(size).fill(null).map(() => Array(size).fill(null));
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      if (dir === 'up') result[c][r] = board[r][c];
      else if (dir === 'down') result[size - 1 - c][r] = board[r][c];
    }
  }
  return result;
}

export function isGameOver(board: (number | null)[][]): boolean {
  for (let r = 0; r < 4; r++) {
    for (let c = 0; c < 4; c++) {
      if (board[r][c] === null) return false;
      if (c < 3 && board[r][c] === board[r][c + 1]) return false;
      if (r < 3 && board[r][c] === board[r + 1][c]) return false;
    }
  }
  return true;
}

export function hasWon(board: (number | null)[][]): boolean {
  return board.some(row => row.some(v => v === 2048));
}